import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Mail, Phone, Car, Package, CheckCircle, ChevronRight, ChevronLeft, MapPin } from 'lucide-react';
import { getApiUrl } from '../../config/api';

// Ensure image URLs use HTTPS to prevent mixed content warnings
const ensureHttps = (url) => {
    if (!url || typeof url !== 'string') return url;
    if (url.startsWith('http://')) return url.replace('http://', 'https://');
    return url;
};

// Resolve banner/image URL for display - ensures HTTPS, handles /uploads/ and S3 URLs
const resolveBannerUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    const u = url.trim();
    if (u.startsWith('http://') || u.startsWith('https://')) return ensureHttps(u);
    if (u.startsWith('/uploads/')) return u; // Relative - same origin
    return u;
};

// Validation helpers
const validateEmail = (email) => {
    if (!email || !email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
    if (email.length > 254) return 'Email is too long';
    return null;
};

const validatePhone = (phone) => {
    if (!phone || !phone.trim()) return null; // Phone is optional
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10) return 'Please enter a valid phone number (at least 10 digits)';
    if (digitsOnly.length > 15) return 'Please enter a valid phone number';
    // Allow UK/international formats: +44, 0, spaces, dashes, parentheses
    const phoneRegex = /^[\d\s\-+()]{10,25}$/;
    if (!phoneRegex.test(phone.trim())) return 'Please enter a valid phone number';
    return null;
};

const validateName = (value, fieldName) => {
    if (!value || !value.trim()) return `${fieldName} is required`;
    if (value.trim().length < 2) return `${fieldName} must be at least 2 characters`;
    if (value.trim().length > 100) return `${fieldName} is too long`;
    return null;
};

export default function PublicEventPage({ identifierOverride }) {
    const navigate = useNavigate();
    const { identifier: paramIdentifier } = useParams();
    const identifier = identifierOverride ?? paramIdentifier;
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [step, setStep] = useState(1); // 1: Overview, 2: Date/Time, 3: Details, 4: Confirmation
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [selectedSalesPerson, setSelectedSalesPerson] = useState('any');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState({});
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: ''
    });
    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [showCalendarMenu, setShowCalendarMenu] = useState(false);
    const [appointment, setAppointment] = useState(null);
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [visitorId, setVisitorId] = useState(null);
    const [sessionId] = useState(() => {
        // Generate or retrieve session ID
        let sessionId = sessionStorage.getItem('event_session_id');
        if (!sessionId) {
            sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            sessionStorage.setItem('event_session_id', sessionId);
        }
        return sessionId;
    });

    useEffect(() => {
        fetchEvent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifier]); // Re-fetch when identifier changes (hostname is checked inside fetchEvent)

    useEffect(() => {
        if (event && event.id) {
            trackVisitor();
        }
    }, [event]);

    useEffect(() => {
        if (step === 2 && (selectedSalesPerson || selectedSalesPerson === null)) {
            fetchAvailableSlots();
        }
    }, [step, selectedSalesPerson, currentMonth]);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Use getApiUrl() to handle custom hostnames properly
            const apiBaseUrl = getApiUrl();
            
            // Check if we're on a custom hostname (accessing via custom domain)
            const hostname = window.location.hostname;
            const defaultDomain = process.env.REACT_APP_DEFAULT_DOMAIN || 'localhost';
            const isCustomHostname = hostname !== 'localhost' && 
                                !hostname.includes('127.0.0.1') && 
                                !hostname.includes('0.0.0.0') &&
                                hostname !== defaultDomain &&
                                (defaultDomain === 'localhost' || !hostname.endsWith('.' + defaultDomain)) &&
                                hostname.includes('.');
            
            let apiUrl;
            
            // If custom hostname and no identifier (or identifier is 'custom-hostname'), use by-hostname endpoint
            if (isCustomHostname && (!identifier || identifier === 'custom-hostname')) {
                apiUrl = `${apiBaseUrl}/events/by-hostname`;
                console.log('Fetching event by hostname:', { hostname, apiUrl });
            } else {
                // Use identifier (slug or short_code)
                if (!identifier) {
                    setError('Event identifier is required.');
                    setLoading(false);
                    return;
                }
                apiUrl = `${apiBaseUrl}/events/public/${identifier}`;
                console.log('Fetching event by identifier:', { identifier, apiUrl, hostname });
            }
            
            // Using relative URL automatically preserves the Host header
            // Browsers don't allow setting Host header manually for security reasons
            const response = await fetch(apiUrl);
            console.log('Response status:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Event data received:', data);
                setEvent(data);
                setTimezone(data.timezone || timezone);
            } else {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                console.error('Error response:', errorData);
                setError(errorData.error || 'Event not found or not available');
            }
        } catch (error) {
            console.error('Error fetching event:', error);
            setError(`Failed to load event: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const trackVisitor = async () => {
        if (!event || !event.id) return;

        try {
            // Get or create visitor ID (persistent across sessions)
            let persistentVisitorId = localStorage.getItem('event_visitor_id');
            if (!persistentVisitorId) {
                persistentVisitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem('event_visitor_id', persistentVisitorId);
            }
            setVisitorId(persistentVisitorId);

            // Parse UTM parameters from URL
            const urlParams = new URLSearchParams(window.location.search);
            const utmParams = {
                utm_source: urlParams.get('utm_source'),
                utm_medium: urlParams.get('utm_medium'),
                utm_campaign: urlParams.get('utm_campaign'),
                utm_term: urlParams.get('utm_term'),
                utm_content: urlParams.get('utm_content')
            };

            // Get voicebox tracking parameters
            const voiceboxParams = {
                voicebox_email_id: urlParams.get('voicebox_email_id'),
                voicebox_campaign_id: urlParams.get('voicebox_campaign_id'),
                voicebox_click_id: urlParams.get('voicebox_click_id')
            };

            // Detect device and browser info
            const userAgent = navigator.userAgent;
            const deviceInfo = {
                device_type: /Mobile|Android|iPhone|iPad/.test(userAgent) ? 'mobile' : 
                            /Tablet|iPad/.test(userAgent) ? 'tablet' : 'desktop',
                browser_name: userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\//)?.[1] || 'Unknown',
                browser_version: userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/)?.[2] || '',
                os_name: userAgent.match(/(Windows|Mac|Linux|Android|iOS)/)?.[1] || 'Unknown',
                screen_resolution: `${window.screen.width}x${window.screen.height}`
            };

            const visitorData = {
                session_id: sessionId,
                visitor_id: persistentVisitorId,
                ip_address: null, // Will be detected server-side
                user_agent: userAgent,
                referrer_url: document.referrer || null,
                landing_page_url: window.location.href,
                ...utmParams,
                ...voiceboxParams,
                ...deviceInfo,
                metadata: {
                    timezone: timezone,
                    language: navigator.language,
                    platform: navigator.platform
                }
            };

            // Track visitor (fire and forget - don't block page load)
            fetch(`${getApiUrl()}/events/${event.id}/visitors`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(visitorData)
            }).catch(err => {
                console.error('Error tracking visitor:', err);
                // Silently fail - don't interrupt user experience
            });
        } catch (error) {
            console.error('Error in trackVisitor:', error);
            // Silently fail
        }
    };

    const fetchAvailableSlots = async () => {
        try {
            const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
            const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
            const params = new URLSearchParams({
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0],
                ...(selectedSalesPerson && selectedSalesPerson !== 'any' && { sales_team_member_id: selectedSalesPerson })
            });

            const response = await fetch(`${getApiUrl()}/appointments/availability/${event.id}?${params}`);
            if (response.ok) {
                const data = await response.json();
                // Group slots by date
                const grouped = {};
                (data.available_slots || []).forEach(slot => {
                    const date = new Date(slot).toISOString().split('T')[0];
                    if (!grouped[date]) grouped[date] = [];
                    grouped[date].push(slot);
                });
                setAvailableSlots(grouped);
            }
        } catch (error) {
            console.error('Error fetching availability:', error);
        }
    };

    const validateForm = () => {
        // Trim values for validation
        const trimmed = {
            firstName: formData.firstName?.trim() || '',
            lastName: formData.lastName?.trim() || '',
            email: formData.email?.trim() || '',
            phone: formData.phone?.trim() || ''
        };
        const errors = {
            firstName: validateName(trimmed.firstName, 'First name'),
            lastName: validateName(trimmed.lastName, 'Last name'),
            email: validateEmail(trimmed.email),
            phone: validatePhone(trimmed.phone)
        };
        setFormErrors(errors);
        return !errors.firstName && !errors.lastName && !errors.email && !errors.phone;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedDate || !selectedTime) {
            setError('Please select a date and time');
            return;
        }

        // Validate form fields before submitting
        if (!validateForm()) {
            setError('Please correct the errors below');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Validate and format date/time
            if (!selectedDate || !selectedTime) {
                setError('Please select both a date and time');
                setSubmitting(false);
                return;
            }

            // Convert 12-hour format (e.g., "2:00 PM") to 24-hour format (e.g., "14:00")
            const convertTo24Hour = (time12h) => {
                const [time, period] = time12h.split(' ');
                const [hours, minutes] = time.split(':');
                let hour24 = parseInt(hours, 10);
                
                if (period === 'PM' && hour24 !== 12) {
                    hour24 += 12;
                } else if (period === 'AM' && hour24 === 12) {
                    hour24 = 0;
                }
                
                return `${hour24.toString().padStart(2, '0')}:${minutes}:00`;
            };

            // Convert time to 24-hour format
            const time24h = convertTo24Hour(selectedTime);
            
            // Create ISO string: YYYY-MM-DDTHH:MM:SS
            const dateTimeString = `${selectedDate}T${time24h}`;
            const appointmentDateTime = new Date(dateTimeString);
            
            // Validate the date is valid
            if (isNaN(appointmentDateTime.getTime())) {
                console.error('Invalid date/time:', { selectedDate, selectedTime, dateTimeString, appointmentDateTime });
                setError('Invalid date or time selected. Please try again.');
                setSubmitting(false);
                return;
            }

            // Trim form data before sending to API
            const trimmedFormData = {
                firstName: formData.firstName?.trim() || '',
                lastName: formData.lastName?.trim() || '',
                email: formData.email?.trim() || '',
                phone: formData.phone?.trim() || ''
            };

            const response = await fetch(`${getApiUrl()}/appointments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    event_id: event.id,
                    sales_team_member_id: selectedSalesPerson === 'any' ? null : selectedSalesPerson,
                    customer_first_name: trimmedFormData.firstName,
                    customer_last_name: trimmedFormData.lastName,
                    customer_email: trimmedFormData.email,
                    customer_phone: trimmedFormData.phone || null,
                    appointment_date: appointmentDateTime.toISOString(),
                    appointment_duration_minutes: selectedSalesPersonData?.appointment_duration_minutes || event.appointment_duration_minutes,
                    selected_vehicle_model_id: selectedVehicle,
                    selected_accessory_ids: selectedAccessories,
                    customer_notes: formData.notes?.trim() || null
                })
            });

            if (response.ok) {
                const data = await response.json();
                setAppointment(data);
                setStep(4);
                
                // Update visitor record with appointment info and contact details
                if (event.id && (sessionId || visitorId)) {
                    fetch(`${getApiUrl()}/events/${event.id}/visitors/appointment`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            appointment_id: data.id,
                            session_id: sessionId,
                            visitor_id: visitorId,
                            first_name: trimmedFormData.firstName,
                            last_name: trimmedFormData.lastName,
                            email: trimmedFormData.email,
                            phone: trimmedFormData.phone || null
                        })
                    }).catch(err => {
                        console.error('Error updating visitor with appointment:', err);
                    });
                }
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to book appointment');
            }
        } catch (error) {
            console.error('Error booking appointment:', error);
            setError('Failed to book appointment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const generateTimeSlots = (date) => {
        if (!event || !date) return [];

        const apiSlots = availableSlots[date] || [];
        if (apiSlots.length === 0) return [];

        return apiSlots.map(isoStr => {
            const dt = new Date(isoStr);
            const timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return {
                time: timeStr,
                datetime: dt,
                iso: isoStr
            };
        });
    };

    const getDaysInMonth = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        // Add days of the month
        const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hasSlots = availableSlots[dateStr] && availableSlots[dateStr].length > 0;
            days.push({
                day,
                date: dateStr,
                hasSlots,
                isToday: dateStr === todayStr,
                isSelected: dateStr === selectedDate
            });
        }
        return days;
    };

    const downloadiCal = () => {
        if (!appointment) return;

        const start = new Date(appointment.appointment_date);
        const end = new Date(start.getTime() + appointment.appointment_duration_minutes * 60000);

        const formatDate = (date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const icalContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//ClientBox//Event Management//EN',
            'BEGIN:VEVENT',
            `UID:${appointment.id}@clientbox`,
            `DTSTAMP:${formatDate(new Date())}`,
            `DTSTART:${formatDate(start)}`,
            `DTEND:${formatDate(end)}`,
            `SUMMARY:${event.name} - ${formData.firstName} ${formData.lastName}`,
            `DESCRIPTION:Appointment for ${event.name}\\nConfirmation Code: ${appointment.confirmation_code}`,
            `LOCATION:${event.group_name || event.oem_name || 'Dealership'}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\r\n');

        const blob = new Blob([icalContent], { type: 'text/calendar' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `appointment-${appointment.confirmation_code}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const getGoogleCalendarUrl = () => {
        if (!appointment) return '#';
        const start = new Date(appointment.appointment_date);
        const end = new Date(start.getTime() + appointment.appointment_duration_minutes * 60000);
        const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: `${event.name}`,
            dates: `${fmt(start)}/${fmt(end)}`,
            details: `Appointment for ${event.name}\nConfirmation Code: ${appointment.confirmation_code}`,
            location: event.group_name || event.oem_name || ''
        });
        return `https://calendar.google.com/calendar/event?${params.toString()}`;
    };

    const getOutlookCalendarUrl = () => {
        if (!appointment) return '#';
        const start = new Date(appointment.appointment_date);
        const end = new Date(start.getTime() + appointment.appointment_duration_minutes * 60000);
        const params = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            subject: `${event.name}`,
            startdt: start.toISOString(),
            enddt: end.toISOString(),
            body: `Appointment for ${event.name}. Confirmation Code: ${appointment.confirmation_code}`,
            location: event.group_name || event.oem_name || ''
        });
        return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading event...</p>
                </div>
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (!event) return null;

    const selectedVehicleModel = event.vehicle_models?.find(v => v.id === selectedVehicle);
    const selectedSalesPersonData = event.sales_team?.find(s => s.id === selectedSalesPerson);
    const selectedAccessoriesData = event.accessories?.filter(a => selectedAccessories.includes(a.id));
    
    // Get effective settings (sales person overrides or event defaults)
    const effectiveSettings = {
        appointment_duration_minutes: selectedSalesPersonData?.appointment_duration_minutes || event.appointment_duration_minutes || 30,
        gap_duration_minutes: selectedSalesPersonData?.gap_duration_minutes || event.gap_duration_minutes || 15,
        availability_start_time: selectedSalesPersonData?.availability_start_time || event.availability_start_time || '09:00',
        availability_end_time: selectedSalesPersonData?.availability_end_time || event.availability_end_time || '17:00',
        availability_days_of_week: selectedSalesPersonData?.availability_days_of_week || event.availability_days_of_week || [1, 2, 3, 4, 5]
    };
    
    const days = getDaysInMonth();
    const timeSlots = selectedDate ? generateTimeSlots(selectedDate) : [];

    // Fix sales team data structure for display
    const salesTeamDisplay = event.sales_team?.map(member => ({
        ...member,
        display_name: member.display_name || member.display_name_final || member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unnamed',
        email: member.email_final || member.email || member.user_email
    })) || [];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header - Calendly style */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {event.branding_logo_url && (
                                <img src={ensureHttps(event.branding_logo_url)} alt="Logo" className="h-10" />
                            )}
                            <div>
                                <h1 className="text-2xl font-semibold text-gray-900">{event.name}</h1>
                                {event.description && (
                                    <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-500">Duration</div>
                            <div className="font-medium text-gray-900">
                                {effectiveSettings.appointment_duration_minutes} min
                                {selectedSalesPersonData?.appointment_duration_minutes && (
                                    <span className="text-xs text-gray-500 ml-1">(personal)</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Event Banner */}
            {event.banner_is_active && (event.banner_text || event.banner_image_url) && (
                <div 
                    className="w-full"
                    style={{ 
                        backgroundColor: event.banner_background_color || '#ffffff',
                        minHeight: '200px',
                        maxHeight: '500px',
                        position: 'relative'
                    }}
                >
                    {event.banner_image_url ? (
                        <div className="relative w-full" style={{ minHeight: '200px', maxHeight: '500px' }}>
                            <img
                                src={resolveBannerUrl(event.banner_image_url)}
                                alt="Event banner"
                                className="w-full h-full object-cover"
                                style={{ maxHeight: '500px', objectPosition: 'center' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            {event.banner_text && (
                                <div 
                                    className="absolute inset-0 flex items-center justify-center p-6"
                                    style={{ color: event.banner_text_color || '#000000' }}
                                >
                                    <p className="text-center font-semibold px-4" style={{ fontSize: `${event.banner_font_size || 24}px`, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                        {event.banner_text}
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : event.banner_text ? (
                        <div 
                            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 flex items-center justify-center"
                            style={{ 
                                color: event.banner_text_color || '#000000',
                                minHeight: '200px',
                                maxHeight: '500px'
                            }}
                        >
                            <p className="text-center font-semibold px-4" style={{ fontSize: `${event.banner_font_size || 24}px` }}>
                                {event.banner_text}
                            </p>
                        </div>
                    ) : null}
                </div>
            )}

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back to events link when on portal domain */}
                {identifierOverride && (
                    <div className="mb-6">
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm font-medium"
                        >
                            ← Back to events
                        </button>
                    </div>
                )}
                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        {[1, 2, 3, 4].map((s) => (
                            <React.Fragment key={s}>
                                <div className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                        step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                        {step > s ? '✓' : s}
                                    </div>
                                    {s < 4 && (
                                        <div className={`w-16 h-1 mx-2 ${
                                            step > s ? 'bg-blue-600' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>Select</span>
                        <span>Time</span>
                        <span>Details</span>
                        <span>Confirm</span>
                    </div>
                </div>

                {error && step !== 4 && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Step 1: Overview & Selection */}
                {step === 1 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 space-y-6">
                        <h2 className="text-xl font-semibold text-gray-900">Select your preferences</h2>

                        {/* Sales Team Selection */}
                        {salesTeamDisplay && salesTeamDisplay.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    <User className="inline mr-2" size={18} />
                                    Who would you like to meet with?
                                </label>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setSelectedSalesPerson('any')}
                                        className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                                            selectedSalesPerson === 'any'
                                                ? 'border-blue-600 bg-blue-50 shadow-sm'
                                                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-gray-900">Any available specialist</div>
                                                <div className="text-sm text-gray-500">We'll match you with the next available team member</div>
                                            </div>
                                            {selectedSalesPerson === 'any' && (
                                                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                                    <CheckCircle size={16} className="text-white" />
                                                </div>
                                            )}
                                        </div>
                                    </button>

                                    {salesTeamDisplay.map((member) => (
                                        <button
                                            key={member.id}
                                            onClick={() => setSelectedSalesPerson(member.id)}
                                            className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                                                selectedSalesPerson === member.id
                                                    ? 'border-blue-600 bg-blue-50 shadow-sm'
                                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium text-gray-900">
                                                        {member.display_name}
                                                    </div>
                                                    {member.email && (
                                                        <div className="text-sm text-gray-500">{member.email}</div>
                                                    )}
                                                    {member.bio && (
                                                        <div className="text-sm text-gray-600 mt-1">{member.bio}</div>
                                                    )}
                                                </div>
                                                {selectedSalesPerson === member.id && (
                                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                                        <CheckCircle size={16} className="text-white" />
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Vehicle Models */}
                        {event.vehicle_models && event.vehicle_models.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    <Car className="inline mr-2" size={18} />
                                    Vehicle of Interest (Optional)
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {event.vehicle_models.map((model) => (
                                        <button
                                            key={model.id}
                                            onClick={() => setSelectedVehicle(selectedVehicle === model.id ? null : model.id)}
                                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                                                selectedVehicle === model.id
                                                    ? 'border-blue-600 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            {model.image_url && (
                                                <img src={ensureHttps(model.image_url)} alt={model.model_name} className="w-full h-32 object-cover rounded mb-2" />
                                            )}
                                            <div className="font-medium text-gray-900">{model.model_name}</div>
                                            {model.model_year && (
                                                <div className="text-sm text-gray-500">{model.model_year}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Accessories */}
                        {event.accessories && event.accessories.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    <Package className="inline mr-2" size={18} />
                                    Accessories (Optional)
                                </label>
                                <div className="space-y-2">
                                    {event.accessories.map((accessory) => (
                                        <label key={accessory.id} className="flex items-center p-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 cursor-pointer transition-all">
                                            <input
                                                type="checkbox"
                                                checked={selectedAccessories.includes(accessory.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedAccessories([...selectedAccessories, accessory.id]);
                                                    } else {
                                                        setSelectedAccessories(selectedAccessories.filter(id => id !== accessory.id));
                                                    }
                                                }}
                                                className="mr-3 w-5 h-5 text-blue-600 rounded"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900">{accessory.name}</div>
                                                {accessory.description && (
                                                    <div className="text-sm text-gray-600">{accessory.description}</div>
                                                )}
                                                {accessory.price && (
                                                    <div className="text-sm font-semibold text-blue-600 mt-1">${accessory.price}</div>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => setStep(2)}
                            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center justify-center gap-2"
                        >
                            Continue
                            <ChevronRight size={20} />
                        </button>
                    </div>
                )}

                {/* Step 2: Date & Time Selection - Calendly Style */}
                {step === 2 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-900">Select date & time</h2>
                            <div className="text-sm text-gray-500">
                                <MapPin size={14} className="inline mr-1" />
                                {timezone}
                            </div>
                        </div>

                        {/* Calendar */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    onClick={() => {
                                        const prev = new Date(currentMonth);
                                        prev.setMonth(prev.getMonth() - 1);
                                        setCurrentMonth(prev);
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </h3>
                                <button
                                    onClick={() => {
                                        const next = new Date(currentMonth);
                                        next.setMonth(next.getMonth() + 1);
                                        setCurrentMonth(next);
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>

                            <div className="grid grid-cols-7 gap-2 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-2">
                                {days.map((day, index) => (
                                    <div key={index}>
                                        {day ? (
                                            <button
                                                onClick={() => {
                                                    setSelectedDate(day.date);
                                                    setSelectedTime(null);
                                                }}
                                                disabled={!day.hasSlots}
                                                className={`w-full aspect-square rounded-lg text-sm font-medium transition-all ${
                                                    day.isSelected
                                                        ? 'bg-blue-600 text-white'
                                                        : day.hasSlots
                                                        ? 'bg-white border-2 border-gray-200 hover:border-blue-600 hover:bg-blue-50 text-gray-900'
                                                        : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                } ${day.isToday ? 'ring-2 ring-blue-200' : ''}`}
                                            >
                                                {day.day}
                                            </button>
                                        ) : (
                                            <div className="aspect-square" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Time Slots */}
                        {selectedDate && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3">
                                    Available times for {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                                        weekday: 'long', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })}
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                                    {timeSlots.map((slot, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedTime(slot.time)}
                                            className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${
                                                selectedTime === slot.time
                                                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-blue-600 hover:bg-blue-50 text-gray-700'
                                            }`}
                                        >
                                            {slot.time}
                                        </button>
                                    ))}
                                </div>
                                {timeSlots.length === 0 && (
                                    <p className="text-center text-gray-500 py-4">No available times for this date</p>
                                )}
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                            >
                                <ChevronLeft size={18} className="inline mr-1" />
                                Back
                            </button>
                            <button
                                onClick={() => selectedDate && selectedTime && setStep(3)}
                                disabled={!selectedDate || !selectedTime}
                                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                Continue
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Enter Details */}
                {step === 3 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter your details</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => {
                                            setFormData({ ...formData, firstName: e.target.value });
                                            if (formErrors.firstName) setFormErrors({ ...formErrors, firstName: validateName(e.target.value, 'First name') });
                                        }}
                                        onBlur={() => setFormErrors({ ...formErrors, firstName: validateName(formData.firstName, 'First name') })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="John"
                                        maxLength={100}
                                        required
                                    />
                                    {formErrors.firstName && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => {
                                            setFormData({ ...formData, lastName: e.target.value });
                                            if (formErrors.lastName) setFormErrors({ ...formErrors, lastName: validateName(e.target.value, 'Last name') });
                                        }}
                                        onBlur={() => setFormErrors({ ...formErrors, lastName: validateName(formData.lastName, 'Last name') })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Smith"
                                        maxLength={100}
                                        required
                                    />
                                    {formErrors.lastName && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Mail className="inline mr-2" size={16} />
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, email: e.target.value });
                                        if (formErrors.email) setFormErrors({ ...formErrors, email: validateEmail(e.target.value) });
                                    }}
                                    onBlur={() => setFormErrors({ ...formErrors, email: validateEmail(formData.email) })}
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="john.smith@example.com"
                                    maxLength={254}
                                    required
                                />
                                {formErrors.email && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Phone className="inline mr-2" size={16} />
                                    Phone <span className="text-gray-500 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => {
                                        setFormData({ ...formData, phone: e.target.value });
                                        if (formErrors.phone) setFormErrors({ ...formErrors, phone: validatePhone(e.target.value) });
                                    }}
                                    onBlur={() => setFormErrors({ ...formErrors, phone: validatePhone(formData.phone) })}
                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="07XXX XXXXXX or +44 7XXX XXXXXX"
                                    maxLength={25}
                                />
                                {formErrors.phone && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Additional Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    rows="3"
                                    maxLength={500}
                                    placeholder="Any additional information you'd like to share... (max 500 characters)"
                                />
                            </div>

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mt-6">
                                <h3 className="font-medium text-gray-900 mb-3">Appointment Summary</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Date & Time:</span>
                                        <span className="font-medium text-gray-900">
                                            {selectedDate && selectedTime && (
                                                <>
                                                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                                                        weekday: 'long', 
                                                        month: 'long', 
                                                        day: 'numeric' 
                                                    })}
                                                    {' at '}
                                                    {selectedTime}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Duration:</span>
                                        <span className="font-medium text-gray-900">
                                            {effectiveSettings.appointment_duration_minutes} minutes
                                            {selectedSalesPersonData?.appointment_duration_minutes && (
                                                <span className="text-xs text-gray-500 ml-1">(personal preference)</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">With:</span>
                                        <span className="font-medium text-gray-900">
                                            {selectedSalesPerson === 'any'
                                                ? 'Any available specialist'
                                                : selectedSalesPersonData
                                                    ? (selectedSalesPersonData.display_name || `${selectedSalesPersonData.first_name} ${selectedSalesPersonData.last_name}`)
                                                    : '—'
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep(2);
                                        setFormErrors({ firstName: '', lastName: '', email: '', phone: '' });
                                    }}
                                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    <ChevronLeft size={18} className="inline mr-1" />
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
                                >
                                    {submitting ? 'Booking...' : 'Confirm Appointment'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Step 4: Confirmation - Calendly Style */}
                {step === 4 && appointment && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="text-green-600" size={48} />
                        </div>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2">You're all set!</h2>
                        <p className="text-gray-600 mb-6">Your appointment has been confirmed</p>

                        <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-600">Confirmation Code</div>
                                    <div className="font-mono font-semibold text-lg text-gray-900">{appointment.confirmation_code}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Date & Time</div>
                                    <div className="font-medium text-gray-900">
                                        {new Date(appointment.appointment_date).toLocaleString('en-US', {
                                            weekday: 'long',
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit'
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Duration</div>
                                    <div className="font-medium text-gray-900">{appointment.appointment_duration_minutes} minutes</div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <div className="relative">
                                <button
                                    onClick={() => setShowCalendarMenu(!showCalendarMenu)}
                                    className="px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-medium transition-colors flex items-center gap-2"
                                >
                                    <Calendar size={18} />
                                    Add to Calendar
                                    <ChevronRight size={16} className={`transition-transform ${showCalendarMenu ? 'rotate-90' : ''}`} />
                                </button>
                                {showCalendarMenu && (
                                    <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                        <a
                                            href={getGoogleCalendarUrl()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                                        >
                                            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                                                <path d="M18.316 5.684H24v12.632h-5.684V5.684z" fill="#1967D2"/>
                                                <path d="M5.684 18.316H0V5.684h5.684v12.632z" fill="#188038"/>
                                                <path d="M18.316 24V18.316H5.684V24h12.632z" fill="#FBBC04"/>
                                                <path d="M5.684 5.684V0h12.632v5.684H5.684z" fill="#EA4335"/>
                                                <path d="M18.316 5.684H24V0h-5.684v5.684z" fill="#1A73E8"/>
                                                <path d="M18.316 18.316H24V24h-5.684v-5.684z" fill="#34A853"/>
                                                <path d="M0 18.316h5.684V24H0v-5.684z" fill="#0D652D"/>
                                                <path d="M0 0h5.684v5.684H0V0z" fill="#C5221F"/>
                                            </svg>
                                            Google Calendar
                                        </a>
                                        <a
                                            href={getOutlookCalendarUrl()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            <svg viewBox="0 0 24 24" className="w-5 h-5">
                                                <path d="M24 7.387v10.478c0 .23-.08.424-.238.583a.793.793 0 01-.583.238h-8.87V6.566h8.87c.23 0 .424.08.583.238.159.159.238.352.238.583z" fill="#0364B8"/>
                                                <path d="M16.281 6.566v12.12H8.534l-.064-.024V5.25l.064-.024h7.747v1.34z" fill="#0A2767"/>
                                                <path d="M24 7.387v1.652l-9.544 5.508L5.37 9.316V7.11l.723.417 8.312 4.799.052.028.064-.028.168-.1L23.59 6.77l.172-.108.238.142c0 .194 0 .39 0 .583z" fill="#28A8EA"/>
                                                <path d="M0 8.502v8.19c0 .44.156.817.469 1.13.312.312.69.469 1.13.469h6.87v-12.12H1.6c-.44 0-.817.156-1.13.469A1.538 1.538 0 000 7.77v.733z" fill="#0078D4"/>
                                                <path d="M8.47 6.17H.892v9.953h7.576V6.17z" fill="#50D9FF" fillOpacity=".2"/>
                                                <path d="M7.49 7.268H1.752v7.77h5.738v-7.77z" fill="white"/>
                                                <path d="M4.621 8.467c.601 0 1.068.155 1.4.466.332.31.498.733.498 1.267 0 .553-.175.989-.524 1.309-.35.32-.825.48-1.426.48h-.76v1.88H2.871V8.467h1.75zm-.812 2.585h.584c.332 0 .587-.085.767-.254.18-.17.27-.41.27-.72 0-.573-.327-.86-.981-.86h-.64v1.834z" fill="#0078D4"/>
                                            </svg>
                                            Outlook Calendar
                                        </a>
                                        <button
                                            onClick={() => { downloadiCal(); setShowCalendarMenu(false); }}
                                            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left rounded-b-lg border-t border-gray-100"
                                        >
                                            <Calendar size={20} className="text-gray-500" />
                                            Download .ics file
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                            >
                                Book Another
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mt-6">
                            A confirmation email has been sent to <strong>{formData.email}</strong>. Please also save your confirmation code for your records.
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}
